export type PlanningSignalSurface = 'card' | 'modal' | 'map-detail' | 'search';
export type PlanningSignalGroup = 'use' | 'works' | 'context' | 'specific';

export interface PlanningTagDetail {
  slug: string;
  label: string;
  confidence?: number;
  evidence: string[];
  evidenceNote?: string;
  sourceFields: string[];
  sourceLayer?: string;
}

export interface DisplayPlanningSignal {
  slug: string;
  label: string;
  group: PlanningSignalGroup;
  priority: number;
  confidence?: number;
  evidence: string[];
  evidenceNote?: string;
  sourceFields: string[];
  sourceLayer?: string;
}

interface DisplaySignalOptions {
  category?: unknown;
  surface?: PlanningSignalSurface;
  maxVisible?: number;
}

const LABEL_OVERRIDES: Record<string, string> = {
  pbsa: 'PBSA',
  hmo: 'HMO',
  ev_charging: 'EV Charging',
  food_drink: 'Food & Drink',
  telecomms: 'Telecomms',
  windows_doors: 'Windows & Doors',
  external_materials: 'Facade / Exterior',
  listed_building: 'Listed Building',
  conservation_area: 'Conservation Area',
  short_let: 'Short Let',
  digital_billboard: 'Digital Billboard',
  residential_flats: 'Residential Flats',
  garage_conversion: 'Garage Conversion',
  opening_alteration: 'Opening Alteration',
  internal_alterations: 'Internal Alterations',
  flues_ventilation: 'Flues & Ventilation',
  bike_storage: 'Bike Storage',
  green_roof: 'Green Roof',
};

const TAG_PRIORITIES: Record<string, number> = {
  pbsa: 5,
  student_accommodation: 6,
  listed_building: 8,
  conservation_area: 11,
  digital_billboard: 12,
  festival: 13,
  edinburgh_festival: 13,
  fringe: 14,
  place_of_worship: 15,
  residential_conversion: 16,
  change_of_use: 18,
  garage_conversion: 20,
  annexe: 21,
  carport: 22,
  pergola: 23,
  mansard_roof: 24,
  green_roof: 25,
  agricultural_building: 26,
  animal_keeping: 27,
  paddock: 28,
  hardstanding: 29,
  digital_signage: 30,
  external_materials: 34,
  opening_alteration: 35,
  roof_works: 42,
  windows_doors: 48,
  internal_alterations: 50,
  dwellinghouse: 90,
};

const CATEGORY_DUPLICATE_TAGS: Record<string, string[]> = {
  new_build_major: ['new_build'],
  change_of_use_conversions: ['change_of_use'],
  short_term_lets: ['short_let'],
  extensions_enlargements: ['house_extension', 'residential_ancillary_extension'],
  demolition: ['demolition', 'site_clearance'],
  outbuildings_ancillary: ['outbuilding', 'ancillary_structure'],
  windows_doors_glazing: ['windows_doors'],
  roof_works_dormers: ['roof_works'],
  mechanical_services: ['flues_ventilation'],
  renewables_energy: ['solar', 'heat_pump', 'ev_charging', 'battery_storage'],
  landscaping_boundaries: ['landscaping', 'boundary_treatment'],
  parking_access: ['parking_access'],
  external_lighting: ['external_lighting'],
  interiors_accessibility: ['internal_alterations', 'accessibility'],
  shopfront_signage: ['advertisement', 'shopfront'],
  telecommunications: ['telecomms'],
};

const CATEGORY_LABEL_DUPLICATE_TAGS: Record<string, string[]> = {
  new_build_and_major_development: CATEGORY_DUPLICATE_TAGS.new_build_major,
  new_build_major_development: CATEGORY_DUPLICATE_TAGS.new_build_major,
  change_of_use_and_conversions: CATEGORY_DUPLICATE_TAGS.change_of_use_conversions,
  change_of_use_conversions: CATEGORY_DUPLICATE_TAGS.change_of_use_conversions,
  short_term_lets: CATEGORY_DUPLICATE_TAGS.short_term_lets,
  extensions_and_enlargements: CATEGORY_DUPLICATE_TAGS.extensions_enlargements,
  extensions_enlargements: CATEGORY_DUPLICATE_TAGS.extensions_enlargements,
  demolition_and_site_clearance: CATEGORY_DUPLICATE_TAGS.demolition,
  demolition_site_clearance: CATEGORY_DUPLICATE_TAGS.demolition,
  outbuildings_and_ancillary_structures: CATEGORY_DUPLICATE_TAGS.outbuildings_ancillary,
  outbuildings_ancillary_structures: CATEGORY_DUPLICATE_TAGS.outbuildings_ancillary,
  windows_doors_and_glazing: CATEGORY_DUPLICATE_TAGS.windows_doors_glazing,
  windows_doors_glazing: CATEGORY_DUPLICATE_TAGS.windows_doors_glazing,
  roof_works_and_dormers: CATEGORY_DUPLICATE_TAGS.roof_works_dormers,
  roof_works_dormers: CATEGORY_DUPLICATE_TAGS.roof_works_dormers,
  mechanical_plant_flues_ventilation: CATEGORY_DUPLICATE_TAGS.mechanical_services,
  renewable_energy_and_sustainability: CATEGORY_DUPLICATE_TAGS.renewables_energy,
  renewable_energy_sustainability: CATEGORY_DUPLICATE_TAGS.renewables_energy,
  renewable_energy_ev_and_heat_pumps: CATEGORY_DUPLICATE_TAGS.renewables_energy,
  renewable_energy_ev_heat_pumps: CATEGORY_DUPLICATE_TAGS.renewables_energy,
  landscaping_gardens_and_boundaries: CATEGORY_DUPLICATE_TAGS.landscaping_boundaries,
  landscaping_gardens_boundaries: CATEGORY_DUPLICATE_TAGS.landscaping_boundaries,
  parking_access_and_highways: CATEGORY_DUPLICATE_TAGS.parking_access,
  parking_access_highways: CATEGORY_DUPLICATE_TAGS.parking_access,
  shopfront_and_signage: CATEGORY_DUPLICATE_TAGS.shopfront_signage,
  shopfront_signage: CATEGORY_DUPLICATE_TAGS.shopfront_signage,
  telecommunications: CATEGORY_DUPLICATE_TAGS.telecommunications,
};

const LOW_INFORMATION_TAGS = new Set(['dwellinghouse']);
const USE_TAGS = new Set([
  'pbsa',
  'student_accommodation',
  'short_let',
  'hmo',
  'hotel',
  'food_drink',
  'retail',
  'office',
  'residential_flats',
  'dwellinghouse',
  'place_of_worship',
  'education',
  'health_care',
  'agricultural_building',
  'animal_keeping',
]);
const WORKS_TAGS = new Set([
  'external_materials',
  'windows_doors',
  'roof_works',
  'internal_alterations',
  'garage_conversion',
  'house_extension',
  'opening_alteration',
  'demolition',
  'landscaping',
  'boundary_treatment',
  'hardstanding',
  'solar',
  'green_roof',
  'flues_ventilation',
]);
const CONTEXT_TAGS = new Set([
  'listed_building',
  'conservation_area',
  'change_of_use',
  'residential_conversion',
  'telecomms',
  'advertisement',
]);

export function normalizePlanningTag(value: unknown): string {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/_{2,}/g, '_');
}

export function normalizePlanningTags(values: unknown): string[] {
  if (!Array.isArray(values)) return [];
  const seen = new Set<string>();
  const normalized: string[] = [];
  values.forEach((value) => {
    const tag = normalizePlanningTag(value);
    if (!tag || seen.has(tag)) return;
    seen.add(tag);
    normalized.push(tag);
  });
  return normalized;
}

const CHANGE_OF_USE_SEARCH_INTENT_PATTERNS = [
  /\bchange\s+of\s+use\b/,
  /\bchange\s+use\b/,
  /\buse\s+class\b/,
  /\bclass\s+[a-z]?\d\b/,
  /\bfrom\s+[a-z0-9 ]+\s+to\b/,
  /\binto\s+(?:flats?|homes?|housing|hotel|office|restaurant|cafe|pub|bar|hmo|student|residential|commercial)\b/,
];

function normalizeTagSearchQuery(value: unknown): string {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function hasChangeOfUseSearchIntent(query: unknown): boolean {
  const normalized = normalizeTagSearchQuery(query);
  if (!normalized) return false;
  return CHANGE_OF_USE_SEARCH_INTENT_PATTERNS.some((pattern) => pattern.test(normalized));
}

export function filterPlanningTagSearchSlugsForQuery(query: unknown, values: unknown): string[] {
  const tags = normalizePlanningTags(values);
  if (hasChangeOfUseSearchIntent(query)) return tags;
  return tags.filter((tag) => tag !== 'change_of_use');
}

export function shouldReplacePlanningTextQueryWithTags(
  query: unknown,
  searchTokens: unknown,
  residualQuery: unknown
): boolean {
  if (typeof residualQuery === 'string' && residualQuery.trim()) return false;
  const normalizedQuery = normalizeTagSearchQuery(query);
  if (!normalizedQuery || !Array.isArray(searchTokens) || searchTokens.length !== 1) return false;
  return normalizeTagSearchQuery(searchTokens[0]) === normalizedQuery;
}

export function buildPlanningSemanticTagUrl(href: unknown, values: unknown): string | null {
  if (typeof href !== 'string' || !href.trim()) return null;
  let url: URL;
  try {
    url = new URL(href, 'https://www.city-scope.co.uk');
  } catch {
    return null;
  }

  const tags = normalizePlanningTags(values);
  ['q', 'tokens', 'search'].forEach((key) => url.searchParams.delete(key));
  if (tags.length > 0) {
    url.searchParams.set('tags', tags.join(','));
  } else {
    url.searchParams.delete('tags');
  }
  return `${url.pathname}${url.search}${url.hash}`;
}

function normalizeTextList(values: unknown, maxItems = 5): string[] {
  const rawValues = Array.isArray(values) ? values : values == null ? [] : [values];
  const seen = new Set<string>();
  const normalized: string[] = [];
  rawValues.forEach((value) => {
    const text = String(value ?? '').replace(/\s+/g, ' ').trim();
    if (!text) return;
    const clamped = text.length > 220 ? `${text.slice(0, 217).trim()}...` : text;
    const key = clamped.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    normalized.push(clamped);
  });
  return normalized.slice(0, maxItems);
}

function normalizeConfidence(value: unknown): number | undefined {
  if (typeof value === 'boolean') return undefined;
  const numeric = typeof value === 'number' ? value : typeof value === 'string' ? Number(value) : Number.NaN;
  if (!Number.isFinite(numeric)) return undefined;
  return Math.max(0, Math.min(1, numeric));
}

export function normalizePlanningTagDetails(values: unknown): PlanningTagDetail[] {
  if (!Array.isArray(values)) return [];
  const detailsBySlug = new Map<string, PlanningTagDetail>();

  values.forEach((value) => {
    if (!value || typeof value !== 'object') return;
    const raw = value as Record<string, unknown>;
    const slug = normalizePlanningTag(raw.slug ?? raw.tag ?? raw.tag_slug);
    if (!slug) return;

    const existing = detailsBySlug.get(slug);
    const confidence = normalizeConfidence(raw.confidence);
    const evidence = normalizeTextList(raw.evidence);
    const sourceFields = normalizeTextList(raw.source_fields ?? raw.sourceFields, 8).map(normalizePlanningTag).filter(Boolean);
    const evidenceNote = String(raw.evidence_note ?? raw.evidenceNote ?? raw.reason ?? raw.rationale ?? '')
      .replace(/\s+/g, ' ')
      .trim();
    const sourceLayer = normalizePlanningTag(raw.source_layer ?? raw.sourceLayer ?? raw.source);

    detailsBySlug.set(slug, {
      slug,
      label: formatPlanningTagLabel(slug),
      confidence: confidence ?? existing?.confidence,
      evidence: [...(existing?.evidence ?? []), ...evidence].filter((item, index, list) => list.indexOf(item) === index).slice(0, 5),
      evidenceNote: evidenceNote || existing?.evidenceNote,
      sourceFields: [...(existing?.sourceFields ?? []), ...sourceFields].filter((item, index, list) => list.indexOf(item) === index).slice(0, 8),
      sourceLayer: sourceLayer || existing?.sourceLayer,
    });
  });

  return Array.from(detailsBySlug.values());
}

export function getPlanningTagDetails(application: Record<string, unknown> | null | undefined): PlanningTagDetail[] {
  return normalizePlanningTagDetails(application?.tag_details ?? application?.tagDetails);
}

export function formatPlanningTagLabel(tag: unknown): string {
  const slug = normalizePlanningTag(tag);
  if (!slug) return '';
  if (LABEL_OVERRIDES[slug]) return LABEL_OVERRIDES[slug];
  return slug
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function normalizeCategoryKey(value: unknown): string {
  if (typeof value === 'object' && value !== null) {
    const category = value as Record<string, unknown>;
    return normalizePlanningTag(category.id ?? category.label ?? category.name);
  }
  return normalizePlanningTag(value);
}

function getCategoryDuplicateTags(category: unknown): Set<string> {
  const key = normalizeCategoryKey(category);
  return new Set([
    ...(CATEGORY_DUPLICATE_TAGS[key] ?? []),
    ...(CATEGORY_LABEL_DUPLICATE_TAGS[key] ?? []),
  ]);
}

function getSignalGroup(slug: string): PlanningSignalGroup {
  if (USE_TAGS.has(slug)) return 'use';
  if (WORKS_TAGS.has(slug)) return 'works';
  if (CONTEXT_TAGS.has(slug)) return 'context';
  return 'specific';
}

function getPriority(slug: string, index: number): number {
  return TAG_PRIORITIES[slug] ?? 60 + index;
}

export function getDisplayPlanningSignals(
  application: Record<string, unknown> | null | undefined,
  options: DisplaySignalOptions = {}
): DisplayPlanningSignal[] {
  const tagDetails = getPlanningTagDetails(application);
  const detailBySlug = new Map(tagDetails.map((detail) => [detail.slug, detail]));
  const tagsFromDetails = tagDetails.map((detail) => detail.slug);
  const tags = normalizePlanningTags(application?.tags).length > 0
    ? normalizePlanningTags(application?.tags)
    : normalizePlanningTags(tagsFromDetails);
  if (tags.length === 0) return [];

  const duplicateTags = getCategoryDuplicateTags(options.category ?? application?.category);
  const hasNonDuplicate = tags.some((tag) => !duplicateTags.has(tag));
  const suppressLowInfo = options.surface !== 'search' && tags.some((tag) => !LOW_INFORMATION_TAGS.has(tag));

  const displayTags = tags.filter((tag) => {
    if (hasNonDuplicate && duplicateTags.has(tag)) return false;
    if (suppressLowInfo && LOW_INFORMATION_TAGS.has(tag)) return false;
    return true;
  });

  const ranked = displayTags
    .map((slug, index) => {
      const detail = detailBySlug.get(slug);
      return {
        slug,
        label: detail?.label || formatPlanningTagLabel(slug),
        group: getSignalGroup(slug),
        priority: getPriority(slug, index),
        confidence: detail?.confidence,
        evidence: detail?.evidence ?? [],
        evidenceNote: detail?.evidenceNote,
        sourceFields: detail?.sourceFields ?? [],
        sourceLayer: detail?.sourceLayer,
      };
    })
    .filter((tag) => tag.label)
    .sort((left, right) => left.priority - right.priority || left.label.localeCompare(right.label));

  if (typeof options.maxVisible === 'number' && options.maxVisible >= 0) {
    return ranked.slice(0, options.maxVisible);
  }
  return ranked;
}

export function getPlanningSignalOverflowCount(
  application: Record<string, unknown> | null | undefined,
  options: DisplaySignalOptions = {}
): number {
  if (typeof options.maxVisible !== 'number') return 0;
  const allSignals = getDisplayPlanningSignals(application, {
    ...options,
    maxVisible: undefined,
  });
  return Math.max(0, allSignals.length - options.maxVisible);
}

export function groupPlanningSignals(signals: DisplayPlanningSignal[]): Record<PlanningSignalGroup, DisplayPlanningSignal[]> {
  return signals.reduce(
    (groups, signal) => {
      groups[signal.group].push(signal);
      return groups;
    },
    {
      use: [],
      works: [],
      context: [],
      specific: [],
    } as Record<PlanningSignalGroup, DisplayPlanningSignal[]>
  );
}

export function getSearchablePlanningTagText(application: Record<string, unknown> | null | undefined): string {
  const tagDetails = getPlanningTagDetails(application);
  const tags = normalizePlanningTags(application?.tags).length > 0
    ? normalizePlanningTags(application?.tags)
    : normalizePlanningTags(tagDetails.map((detail) => detail.slug));
  if (tags.length === 0) return '';
  return tags
    .flatMap((tag) => {
      const detail = tagDetails.find((item) => item.slug === tag);
      return [
        tag.replace(/_/g, ' '),
        formatPlanningTagLabel(tag),
        detail?.evidenceNote,
        ...(detail?.evidence ?? []),
      ];
    })
    .filter(Boolean)
    .join(' ');
}
